if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const itemKey = this.submitButton.getAttribute('data-item-key');
        const itemQuantity = this.submitButton.getAttribute('data-item-quantity');
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // LIO-617: never fall back to the first variant when no size has been
        // chosen — show the size-required error instead of adding to cart.
        if (this.requireOptionSelection()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (itemQuantity) {
            formData.append('quantity', itemQuantity);
          }
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        if (itemKey) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: itemKey,
              quantity: 0
            })
          });
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // LIO-364: `state` is 'add' | 'preorder' | 'ready-to-ship', read off the
      // server-rendered button's data-cta-state so Liquid stays the source of
      // truth for which label a variant gets. Booleans are still accepted for
      // back-compat (true === 'preorder').
      toggleSubmitButton(disable = true, state = 'preorder', text) {
        if (state === true) state = 'preorder';
        if (state === false) state = 'add';

        if (disable) {
          this.submitButton.removeAttribute('preorder');
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          return;
        }

        this.submitButton.removeAttribute('disabled');
        this.submitButton.dataset.ctaState = state;

        if (state === 'preorder') {
          this.submitButton.setAttribute('preorder', 'preorder');
          this.submitButtonText.textContent = window.variantStrings.pre_order;
        } else if (state === 'ready-to-ship') {
          this.submitButton.removeAttribute('preorder');
          this.submitButtonText.textContent = window.variantStrings.readyToShip;
        } else {
          this.submitButton.removeAttribute('preorder');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      // LIO-617: returns true (and shows the error) when an option dropdown is
      // still unselected, so the caller can stop the add to cart.
      requireOptionSelection() {
        const unselected = this.closest('.product__info-container')?.querySelector(
          '.product-variant-selector__item-dropdown:not(.is-selected)'
        );
        if (!unselected) return false;

        const item = unselected.closest('.product-variant-selector__item');
        window.FakeSelector?.showSelectionError(item);

        const rect = item?.getBoundingClientRect();
        if (rect && (rect.top < 0 || rect.bottom > window.innerHeight)) {
          item.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return true;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
